import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { OurAuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { QuickTourProgress } from 'app/core/user/user.types';
import { SAI_QUICK_TOUR_AGENT_FORM_COACH_STEPS } from 'app/ui/sai-quick-tour/sai-quick-tour-agent-form-coach.steps';
import { SAI_QUICK_TOUR_DEPARTMENT_FORM_COACH_STEPS } from 'app/ui/sai-quick-tour/sai-quick-tour-department-form-coach.steps';
import { SAI_QUICK_TOUR_DIVISION_FORM_COACH_STEPS } from 'app/ui/sai-quick-tour/sai-quick-tour-division-form-coach.steps';
import { SAI_QUICK_TOUR_DS_FORM_COACH_STEPS } from 'app/ui/sai-quick-tour/sai-quick-tour-ds-form-coach.steps';
import {
    SaiQuickTourFormCoachKind,
    SaiQuickTourFormCoachState,
    SaiQuickTourFormCoachStep,
} from 'app/ui/sai-quick-tour/sai-quick-tour-form-coach.types';
import {
    filterTourStepsByPermission,
} from 'app/ui/sai-quick-tour/sai-quick-tour-permission.util';
import { SAI_QUICK_TOUR_STEPS } from 'app/ui/sai-quick-tour/sai-quick-tour.steps';
import {
    isTourRoutePreloaded,
    preloadTourRoute,
} from 'app/ui/sai-quick-tour/sai-quick-tour-preload';
import { resolveResumeIndex } from 'app/ui/sai-quick-tour/sai-quick-tour-progress.util';
import {
    findBestTourTargetElement,
    isSaiQuickTourMobileViewport,
    isSaiQuickTourNavTarget,
    isSaiQuickTourPageHeaderCtaTarget,
    isTourTargetVisible,
    resolveTourMeasureElement,
    scrollTourTargetIntoView,
    shouldDeferNavStepRouteOnMobile,
} from 'app/ui/sai-quick-tour/sai-quick-tour-layout.util';
import {
    SaiQuickTourStep,
    SaiQuickTourTargetRect,
} from 'app/ui/sai-quick-tour/sai-quick-tour.types';
import {
    BehaviorSubject,
    filter,
    firstValueFrom,
    take,
} from 'rxjs';
import { environment } from 'environments/environment';
import { isQuickTourAutoStartBlockedRoute } from 'app/ui/sai-quick-tour/sai-quick-tour-blocked-routes.util';

const TARGET_POLL_MS = 80;
const TARGET_POLL_MAX = 50;
/** Permission-gated page-header CTAs (e.g. Add Data Source) may mount after the first probe. */
const TARGET_POLL_MAX_LATE = 100;
const NAV_SETTLE_COLD_MS = 80;
const MOBILE_NAV_SETTLE_MS = 220;
/** Remeasure after hierarchy/list content replaces empty-state / skeleton targets. */
const TARGET_REMEASURE_DELAYS_MS = [120, 320, 700] as const;
const PRELOAD_LOOKAHEAD = 2;
/** Match `.sai-quick-tour__card` opacity transition. */
const CARD_FADE_MS = 180;

const TOUR_ROLES = new Set(['company']);

const EMPTY_FORM_COACH: SaiQuickTourFormCoachState = {
    active: false,
    kind: null,
    index: 0,
    steps: [],
};

@Injectable({ providedIn: 'root' })
export class SaiQuickTourService {
    /** Full canonical step list (13 steps). */
    readonly canonicalSteps: readonly SaiQuickTourStep[] = SAI_QUICK_TOUR_STEPS;

    private readonly _enabled = environment.showQuickTour;

    private readonly _visibleSteps$ = new BehaviorSubject<readonly SaiQuickTourStep[]>(SAI_QUICK_TOUR_STEPS);
    private readonly _active$ = new BehaviorSubject<boolean>(false);
    private readonly _stepIndex$ = new BehaviorSubject<number>(0);
    private readonly _targetRect$ = new BehaviorSubject<SaiQuickTourTargetRect | null>(null);
    /** Tip card opacity — false during route/tab changes so Next/Back can fade out → move → fade in. */
    private readonly _cardVisible$ = new BehaviorSubject<boolean>(true);
    private readonly _formCoach$ = new BehaviorSubject<SaiQuickTourFormCoachState>(EMPTY_FORM_COACH);

    readonly active$ = this._active$.asObservable();
    readonly visibleSteps$ = this._visibleSteps$.asObservable();
    readonly stepIndex$ = this._stepIndex$.asObservable();
    readonly targetRect$ = this._targetRect$.asObservable();
    readonly cardVisible$ = this._cardVisible$.asObservable();
    readonly formCoach$ = this._formCoach$.asObservable();

    private readonly _router = inject(Router);
    private readonly _authService = inject(OurAuthService);
    private readonly _userService = inject(UserService);
    private readonly _fuseNavigationService = inject(FuseNavigationService);

    private _autoStartScheduled = false;
    private _targetPollHandle: number | null = null;
    private _navigateTimer: number | null = null;
    private _remeasureTimers: number[] = [];
    private _profileWarmStarted = false;
    private _stepGeneration = 0;
    private _transitioning = false;
    private _mobileNavOpenedByTour = false;

    get steps(): readonly SaiQuickTourStep[] {
        return this._visibleSteps$.value;
    }

    get isActive(): boolean {
        return this._active$.value;
    }

    get currentStepIndex(): number {
        return this._stepIndex$.value;
    }

    get currentStep(): SaiQuickTourStep | null {
        return this.steps[this._stepIndex$.value] ?? null;
    }

    get formCoach(): SaiQuickTourFormCoachState {
        return this._formCoach$.value;
    }

    get formCoachStep(): SaiQuickTourFormCoachStep | null {
        const state = this._formCoach$.value;
        if (!state.active) {
            return null;
        }
        return state.steps[state.index] ?? null;
    }

    isOnAddDivisionStep(): boolean {
        return this.isActive && this.currentStep?.id === 'add-division';
    }

    isOnAddDepartmentStep(): boolean {
        return this.isActive && this.currentStep?.id === 'add-department';
    }

    /** True on the Create a data source tour step (1-based step 7). */
    isOnAddDataSourceStep(): boolean {
        return this.isActive && this.currentStep?.id === 'add-data-source';
    }

    isOnAddAgentStep(): boolean {
        return this.isActive && this.currentStep?.id === 'add-agent';
    }

    /** Start the in-dialog Add Division field coach (call from the Add dialog). */
    startDivisionFormCoach(): void {
        this._startFormCoach('division', SAI_QUICK_TOUR_DIVISION_FORM_COACH_STEPS);
    }

    /** Start the in-dialog Add Department field coach (call from the Add dialog). */
    startDepartmentFormCoach(): void {
        this._startFormCoach('department', SAI_QUICK_TOUR_DEPARTMENT_FORM_COACH_STEPS);
    }

    /** Start the in-dialog Add Data Source field coach (call from the Add dialog). */
    startDataSourceFormCoach(): void {
        this._startFormCoach('data-source', SAI_QUICK_TOUR_DS_FORM_COACH_STEPS);
    }

    /** Start the in-dialog Add Agent field coach (call from the Add dialog). */
    startAgentFormCoach(): void {
        this._startFormCoach('agent', SAI_QUICK_TOUR_AGENT_FORM_COACH_STEPS);
    }

    private _startFormCoach(
        kind: Exclude<SaiQuickTourFormCoachKind, null>,
        steps: readonly SaiQuickTourFormCoachStep[],
    ): void {
        if (!this.isActive) {
            return;
        }
        this._formCoach$.next({
            active: true,
            kind,
            index: 0,
            steps,
        });
    }

    nextFormCoach(): void {
        const state = this._formCoach$.value;
        if (!state.active) {
            return;
        }
        if (state.index >= state.steps.length - 1) {
            this.endFormCoach();
            return;
        }
        this._formCoach$.next({ ...state, index: state.index + 1 });
    }

    backFormCoach(): void {
        const state = this._formCoach$.value;
        if (!state.active || state.index <= 0) {
            return;
        }
        this._formCoach$.next({ ...state, index: state.index - 1 });
    }

    endFormCoach(): void {
        if (!this._formCoach$.value.active) {
            return;
        }
        this._formCoach$.next(EMPTY_FORM_COACH);
    }

    /**
     * Call when an Add dialog closes (save or cancel). Ends any form coach and advances
     * past the matching tour step so Cancel does not leave the tip stuck on “Click Add …”.
     */
    completeAddDialogStep(stepId: string): void {
        if (!this.isActive) {
            this.endFormCoach();
            return;
        }
        this.endFormCoach();
        if (this.currentStep?.id === stepId) {
            this.next();
        }
    }

    isCompletedForCurrentUser(): boolean {
        return this._userService.isQuickTourCompleted();
    }

    /** True when a previous run was closed or refreshed away and has a step to resume from. */
    hasSavedProgress(): boolean {
        return this._userService.readQuickTourProgress() !== null;
    }

    start(fromIndex = 0): void {
        if (!this._enabled) {
            return;
        }
        void this._startWithPermissions(fromIndex);
    }

    /** Open the tour on the step a previous run was left on, or step 1 when there is none. */
    resume(): void {
        if (!this._enabled) {
            return;
        }
        void this._startWithPermissions(0, this._userService.readQuickTourProgress());
    }

    private async _startWithPermissions(
        fromIndex: number,
        progress: QuickTourProgress | null = null,
    ): Promise<void> {
        const visibleSteps = await this._resolveVisibleSteps();
        this._visibleSteps$.next(visibleSteps);

        this._clearTimers();
        this._profileWarmStarted = false;
        this._stepGeneration += 1;
        this._transitioning = false;
        const startIndex = resolveResumeIndex(visibleSteps, progress, fromIndex);
        const step = visibleSteps[startIndex];
        if (!step) {
            return;
        }

        // Hide the tip until the spotlight target (or centered settle) is ready — after a
        // refresh the layout is still mounting, so showing immediately parks the card in
        // the viewport center and `isCentered` used to stick there.
        this._cardVisible$.next(false);
        this._stepIndex$.next(startIndex);
        this._active$.next(true);
        void this._openAtStep(step);
    }

    private async _openAtStep(step: SaiQuickTourStep): Promise<void> {
        this._transitioning = true;
        try {
            const showPromise = this._showStep(step);
            await this._waitUntilStepReady(step);
            this.refreshTargetRect({ scroll: true });
            await this._waitFrames(2);
            if (!this.isActive) {
                return;
            }
            this._cardVisible$.next(true);
            await showPromise;
        } finally {
            this._transitioning = false;
        }
    }

    private async _resolveVisibleSteps(): Promise<readonly SaiQuickTourStep[]> {
        try {
            const perms = await firstValueFrom(
                this._userService.getPermissionsForGuard().pipe(take(1)),
            );
            return filterTourStepsByPermission(SAI_QUICK_TOUR_STEPS, perms);
        } catch {
            return filterTourStepsByPermission(SAI_QUICK_TOUR_STEPS, {});
        }
    }

    finish(): void {
        this._userService.clearQuickTourProgress();
        this._userService.markQuickTourCompleted().subscribe();
        this._stopInternal();
    }

    skip(): void {
        this.finish();
    }

    /**
     * Dismiss the tour without completing it (X button, Esc). The current step is kept so the
     * next run — after a refresh, a later sign-in, or **Start Tour** — opens right here.
     */
    close(): void {
        this._saveProgress();
        this._stopInternal();
    }

    next(): void {
        const nextIndex = this._stepIndex$.value + 1;
        if (nextIndex >= this.steps.length) {
            this.finish();
            return;
        }
        void this._goToStep(nextIndex);
    }

    back(): void {
        const prevIndex = this._stepIndex$.value - 1;
        if (prevIndex < 0) {
            return;
        }
        void this._goToStep(prevIndex);
    }

    restart(): void {
        if (!this._enabled) {
            return;
        }
        this.resume();
    }

    scheduleAutoStartIfEligible(): void {
        if (!this._enabled) {
            return;
        }
        if (this._autoStartScheduled || this.isActive) {
            return;
        }
        if (!this._isEligibleRole()) {
            return;
        }

        this._autoStartScheduled = true;

        this._userService.syncQuickTourCompletedFromProfile().pipe(take(1)).subscribe(() => {
            this._scheduleAutoStartAfterTourStateSync();
        });
    }

    private _scheduleAutoStartAfterTourStateSync(): void {
        if (this.isActive || !this._isEligibleRole()) {
            return;
        }

        const forceTour = this._router.parseUrl(this._router.url).queryParams['quickTour'];
        const shouldForce =
            forceTour === '1' ||
            forceTour === 'true' ||
            forceTour === 'reset';
        // An interrupted run outranks the completion flag: the user closed the tour, never
        // finished or skipped it, so it owes them the rest of it.
        const shouldResume = this.hasSavedProgress();
        if (!shouldForce && !shouldResume && this.isCompletedForCurrentUser()) {
            return;
        }

        const url = this._router.url.split('?')[0];
        if (this._isBlockedRoute(url) && !shouldForce) {
            this._waitForEligibleRouteThenAutoStart(shouldForce, shouldResume);
            return;
        }

        this._waitForEligibleRouteThenAutoStart(shouldForce, shouldResume);
    }

    private _waitForEligibleRouteThenAutoStart(
        shouldForce: boolean,
        shouldResume: boolean,
    ): void {
        this._router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            filter((event: NavigationEnd) => !this._isBlockedRoute(event.urlAfterRedirects.split('?')[0])),
            take(1),
        ).subscribe(() => {
            window.setTimeout(() => {
                if (!this.isActive && this._isEligibleRole()) {
                    if (
                        !shouldForce &&
                        !shouldResume &&
                        this.isCompletedForCurrentUser()
                    ) {
                        return;
                    }
                    if (shouldForce) {
                        this.start(0);
                        return;
                    }
                    this.resume();
                }
            }, 400);
        });
    }

    refreshTargetRect(options?: { scroll?: boolean }): void {
        const step = this.currentStep;
        if (!step?.target || step.centered) {
            return;
        }
        const el = findBestTourTargetElement(step.target);
        if (!el) {
            this._targetRect$.next(null);
            return;
        }
        const measureEl = resolveTourMeasureElement(el);
        if (options?.scroll) {
            const isMobile = isSaiQuickTourMobileViewport(window.innerWidth);
            scrollTourTargetIntoView(measureEl, isMobile, step.target);
        }
        if (!isTourTargetVisible(measureEl)) {
            this._targetRect$.next(null);
            return;
        }
        const rect = measureEl.getBoundingClientRect();
        if (rect.width < 2 || rect.height < 2) {
            this._targetRect$.next(null);
            return;
        }
        const next: SaiQuickTourTargetRect = {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        };
        const prev = this._targetRect$.value;
        if (
            prev &&
            Math.abs(prev.top - next.top) < 0.5 &&
            Math.abs(prev.left - next.left) < 0.5 &&
            Math.abs(prev.width - next.width) < 0.5 &&
            Math.abs(prev.height - next.height) < 0.5
        ) {
            return;
        }
        this._targetRect$.next(next);
    }

    onTargetClicked(): void {
        const step = this.currentStep;
        if (step?.advanceMode === 'click-target') {
            window.setTimeout(() => this.next(), 400);
        }
    }

    /**
     * Fade tip out → change step/route/tab → fade tip in once the destination is ready.
     * Same path for Next and Back (e.g. Profile form ↔ Hierarchy).
     */
    private async _goToStep(index: number): Promise<void> {
        if (this._transitioning || !this.isActive) {
            return;
        }
        this._transitioning = true;
        const from = this.steps[this._stepIndex$.value];
        const to = this.steps[index];
        const destinationChanges = this._destinationKey(from) !== this._destinationKey(to);

        try {
            if (destinationChanges) {
                this._cardVisible$.next(false);
                await this._delay(CARD_FADE_MS);
            }

            this._stepIndex$.next(index);
            // Run navigate + target poll together so sidebar tips (e.g. Profile)
            // appear as soon as the chrome target exists — do not wait for the
            // destination lazy route / API settle first.
            const showPromise = this._showStep(to);

            if (destinationChanges) {
                await this._waitUntilStepReady(to);
                this._cardVisible$.next(true);
            }

            await showPromise;
        } finally {
            this._transitioning = false;
        }
    }

    private async _showStep(step: SaiQuickTourStep): Promise<void> {
        this._clearTargetPoll();
        this._clearRemeasureTimers();
        this._stepGeneration += 1;
        const generation = this._stepGeneration;

        // Written on every step, not only on close, so a page refresh (or a crash) resumes here.
        this._saveProgress(step);

        this._preloadUpcoming(this._stepIndex$.value);

        if (step.centered || !step.target) {
            this._targetRect$.next(null);
        } else {
            this._targetRect$.next(null);
            if (!isSaiQuickTourNavTarget(step.target)) {
                this._closeMobileNavIfTourOpened();
            }
            this._pollForTarget(step.target);
        }

        const deferRoute = shouldDeferNavStepRouteOnMobile(step.target, step.route);
        if (step.route && !deferRoute) {
            await this._navigateForStep(step);
        }

        if (generation !== this._stepGeneration) {
            return;
        }

        await this._ensureMobileNavForStep(step);

        if (generation !== this._stepGeneration) {
            return;
        }

        if (!step.centered && step.target) {
            this.refreshTargetRect({ scroll: true });
        }
    }

    private _saveProgress(step: SaiQuickTourStep | null = this.currentStep): void {
        if (!step) {
            return;
        }
        this._userService.saveQuickTourProgress({
            stepId: step.id,
            stepIndex: this._stepIndex$.value,
        });
    }

    private _preloadUpcoming(fromIndex: number): void {
        const seen = new Set<string>();
        for (let i = fromIndex + 1; i <= fromIndex + PRELOAD_LOOKAHEAD && i < this.steps.length; i++) {
            const route = this.steps[i]?.route;
            if (!route || seen.has(route)) {
                continue;
            }
            seen.add(route);
            void preloadTourRoute(route);
            if (route.startsWith('/pages/profile')) {
                this._warmProfileData();
            }
        }
    }

    private _warmProfileData(): void {
        if (this._profileWarmStarted) {
            return;
        }
        this._profileWarmStarted = true;
        this._userService.getcompany().pipe(take(1)).subscribe({
            error: () => {
                this._profileWarmStarted = false;
            },
        });
    }

    private _pollForTarget(selector: string): void {
        let attempts = 0;
        let didScroll = false;
        this._clearTargetPoll();
        this._clearRemeasureTimers();
        const maxAttempts = isSaiQuickTourPageHeaderCtaTarget(selector)
            ? TARGET_POLL_MAX_LATE
            : TARGET_POLL_MAX;

        const probe = (): boolean => {
            const el = findBestTourTargetElement(selector);
            if (!el) {
                return false;
            }
            // Off-screen nav rows are valid candidates — scroll first, then require visibility.
            this.refreshTargetRect({ scroll: !didScroll });
            didScroll = true;
            if (!this._targetRect$.value) {
                // Still invisible after scroll (e.g. drawer not open yet).
                return false;
            }
            this._scheduleFollowUpRemeasures();
            return true;
        };

        if (probe()) {
            return;
        }

        this._targetPollHandle = window.setInterval(() => {
            attempts += 1;
            if (probe()) {
                this._clearTargetPoll();
                return;
            }
            if (attempts >= maxAttempts) {
                this._targetRect$.next(null);
                this._clearTargetPoll();
            }
        }, TARGET_POLL_MS);
    }

    /** Wait until spotlight target exists (or a short settle for centered steps). */
    private async _waitUntilStepReady(step: SaiQuickTourStep): Promise<void> {
        if (step.centered || !step.target) {
            await this._waitFrames(2);
            return;
        }

        const selector = step.target;
        const deadline = Date.now() + TARGET_POLL_MS * TARGET_POLL_MAX;
        while (Date.now() < deadline) {
            const el = findBestTourTargetElement(selector);
            if (el) {
                this.refreshTargetRect({ scroll: true });
                if (this._targetRect$.value) {
                    this._scheduleFollowUpRemeasures();
                    await this._waitFrames(1);
                    return;
                }
            }
            await this._delay(TARGET_POLL_MS);
        }
    }

    private async _navigateForStep(step: SaiQuickTourStep): Promise<boolean> {
        if (this._navigateTimer) {
            clearTimeout(this._navigateTimer);
            this._navigateTimer = null;
        }

        const targetUrl = this._urlForStep(step);
        if (this._isCurrentUrl(targetUrl)) {
            await this._waitFrames(1);
            return true;
        }

        const wasWarm = isTourRoutePreloaded(step.route!);
        await preloadTourRoute(step.route!);

        const ok = await this._router.navigateByUrl(targetUrl);
        if (!ok) {
            return false;
        }

        if (wasWarm) {
            await this._waitFrames(2);
            return true;
        }

        await new Promise<void>((resolve) => {
            this._navigateTimer = window.setTimeout(() => {
                this._navigateTimer = null;
                resolve();
            }, NAV_SETTLE_COLD_MS);
        });
        return true;
    }

    private _destinationKey(step: SaiQuickTourStep): string {
        if (!step.route) {
            return `stay:${step.id}`;
        }
        return this._urlForStep(step);
    }

    private _urlForStep(step: SaiQuickTourStep): string {
        // Always pass queryParams object so missing params clear ?tabIndex= (Profile form vs Hierarchy).
        const tree = this._router.createUrlTree([step.route!], {
            queryParams: step.queryParams ?? {},
        });
        return this._router.serializeUrl(tree);
    }

    private _isCurrentUrl(targetUrl: string): boolean {
        const currentSerialized = this._router.serializeUrl(this._router.parseUrl(this._router.url));
        return currentSerialized === targetUrl;
    }

    private _delay(ms: number): Promise<void> {
        return new Promise((resolve) => {
            window.setTimeout(resolve, ms);
        });
    }

    private _waitFrames(count: number): Promise<void> {
        return new Promise((resolve) => {
            const tick = (left: number): void => {
                if (left <= 0) {
                    resolve();
                    return;
                }
                requestAnimationFrame(() => tick(left - 1));
            };
            tick(count);
        });
    }

    private _stopInternal(): void {
        this._stepGeneration += 1;
        this._transitioning = false;
        this._cardVisible$.next(true);
        this.endFormCoach();
        this._closeMobileNavIfTourOpened();
        this._clearTimers();
        this._active$.next(false);
        this._targetRect$.next(null);
    }

    private async _ensureMobileNavForStep(step: SaiQuickTourStep): Promise<void> {
        if (!isSaiQuickTourMobileViewport(window.innerWidth) || !isSaiQuickTourNavTarget(step.target)) {
            return;
        }

        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation',
            );
        if (!navigation) {
            return;
        }

        if (!navigation.opened) {
            navigation.open();
            this._mobileNavOpenedByTour = true;
        }

        navigation.refresh();
        await this._delay(MOBILE_NAV_SETTLE_MS);
        await this._waitFrames(2);
    }

    private _closeMobileNavIfTourOpened(): void {
        if (!this._mobileNavOpenedByTour) {
            return;
        }

        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation',
            );
        if (navigation?.opened) {
            navigation.close();
        }
        this._mobileNavOpenedByTour = false;
    }

    private _clearTimers(): void {
        this._clearTargetPoll();
        this._clearRemeasureTimers();
        if (this._navigateTimer) {
            clearTimeout(this._navigateTimer);
            this._navigateTimer = null;
        }
    }

    private _clearTargetPoll(): void {
        if (this._targetPollHandle) {
            clearInterval(this._targetPollHandle);
            this._targetPollHandle = null;
        }
    }

    /**
     * Hierarchy / list pages often swap empty-state CTAs for the real toolbar button
     * after the first paint — remeasure so the spotlight does not stick to stale coords.
     */
    private _scheduleFollowUpRemeasures(): void {
        this._clearRemeasureTimers();
        const generation = this._stepGeneration;
        for (const delay of TARGET_REMEASURE_DELAYS_MS) {
            const handle = window.setTimeout(() => {
                if (generation !== this._stepGeneration || !this.isActive) {
                    return;
                }
                this.refreshTargetRect();
            }, delay);
            this._remeasureTimers.push(handle);
        }
    }

    private _clearRemeasureTimers(): void {
        for (const handle of this._remeasureTimers) {
            clearTimeout(handle);
        }
        this._remeasureTimers = [];
    }

    private _isEligibleRole(): boolean {
        return TOUR_ROLES.has(this._authService.userRole);
    }

    private _isBlockedRoute(path: string): boolean {
        return isQuickTourAutoStartBlockedRoute(path);
    }
}
